import { ENTRY_TYPE_LABEL } from '../employeeTimeEntryConstants';
import { isFilterMultiSelectNone } from '../../filterMultiSelectUtils';
import { derivePayrollAllowances, roundBookingsAllowanceHours } from '../../../utils/employeePayrollBookingsExport';
import { derivePayrollSummary, normalizeCompensationType, roundPayrollMoney, type PayrollHoursByType } from '../../../utils/payrollSummaryCalc';
import type { EmployeeCompensationType } from '../../../utils/payrollSummaryCalc';

export type PayrollExportIssue = {
  employeeId: string;
  employeeName: string;
  entryDate: string;
  entryType: string;
  message: string;
};

export const PAYROLL_SUMMARY_TYPES = ['work', 'vacation', 'sickness', 'public_holiday', 'overtime_comp'] as const;

export type PayrollEmployeeSummary = {
  id: string;
  name: string;
  position: string;
  color_idx: number;
  is_archived: boolean;
  compensation_type: EmployeeCompensationType;
  hourly_wage: number;
  monthly_salary: number;
  allowance_per_hour: number;
  vma_tax_free_per_day: number;
  overnight_tax_free_per_day: number;
  work_days: number;
  hours_by_type: PayrollHoursByType;
  allowance_hours: number;
  net_work_hours: number;
  billable_hours: number;
  allowance_hourly_pay: number;
  vma_pay: number;
  overnight_pay: number;
  /** Summe aller Zulagen (Stunde + VMA + ÜN). */
  allowance_pay: number;
  wage_pay: number;
  total_hours: number;
  /** Netto-Summe Be- und Abzüge (Bezüge minus Abzüge) im Zeitraum. */
  payroll_adjustment_net: number;
  forecasted_gross_pay: number;
};

export type ExportCandidate = {
  id: string;
  name: string;
  position: string;
  color_idx: number;
  is_archived: boolean;
  compensation_type: EmployeeCompensationType;
};

export function normalizeExportCandidate(raw: {
  id: string;
  name: string;
  position?: string | null;
  color_idx?: number | null;
  is_archived?: boolean | null;
  compensation_type?: string | null;
}): ExportCandidate {
  return {
    id: raw.id,
    name: raw.name,
    position: String(raw.position ?? '').trim(),
    color_idx: Number.isFinite(Number(raw.color_idx)) ? Number(raw.color_idx) : 0,
    is_archived: Boolean(raw.is_archived),
    compensation_type: normalizeCompensationType(raw.compensation_type),
  };
}

export function normalizePayrollEmployeeSummary(
  raw: Partial<PayrollEmployeeSummary> & {
    id: string;
    name: string;
    hours_by_type: PayrollHoursByType;
  },
): PayrollEmployeeSummary {
  const hours_by_type: PayrollHoursByType = {
    work: raw.hours_by_type?.work ?? 0,
    vacation: raw.hours_by_type?.vacation ?? 0,
    sickness: raw.hours_by_type?.sickness ?? 0,
    public_holiday: raw.hours_by_type?.public_holiday ?? 0,
    overtime_comp: raw.hours_by_type?.overtime_comp ?? 0,
  };
  const compensation_type = normalizeCompensationType(raw.compensation_type);
  const hourly_wage = Number(raw.hourly_wage) || 0;
  const monthly_salary = Number(raw.monthly_salary) || 0;
  const allowance_per_hour = compensation_type === 'salary' ? 0 : Number(raw.allowance_per_hour) || 0;
  const vma_tax_free_per_day = Number(raw.vma_tax_free_per_day) || 0;
  const overnight_tax_free_per_day = Number(raw.overnight_tax_free_per_day) || 0;
  const derived = derivePayrollSummary(hours_by_type, hourly_wage, allowance_per_hour, {
    compensationType: compensation_type,
    monthlySalary: monthly_salary,
  });
  const allowance_hours =
    Number(raw.allowance_hours) > 0 ? Number(raw.allowance_hours) : roundBookingsAllowanceHours(hours_by_type);
  const work_days = Number.isFinite(Number(raw.work_days)) ? Math.max(0, Math.floor(Number(raw.work_days))) : 0;

  const allowancesFromApi =
    raw.allowance_hourly_pay != null || raw.vma_pay != null || raw.overnight_pay != null
      ? {
          allowance_hours,
          work_days,
          allowance_hourly_pay: Number(raw.allowance_hourly_pay) || 0,
          vma_pay: Number(raw.vma_pay) || 0,
          overnight_pay: Number(raw.overnight_pay) || 0,
          total_allowance_pay: Number(raw.allowance_pay) || 0,
        }
      : derivePayrollAllowances({
          allowance_hours,
          work_days,
          allowance_per_hour,
          vma_tax_free_per_day,
          overnight_tax_free_per_day,
        });

  const allowance_pay =
    Number(raw.allowance_pay) > 0
      ? Number(raw.allowance_pay)
      : allowancesFromApi.total_allowance_pay;
  const payroll_adjustment_net = Number(raw.payroll_adjustment_net) || 0;
  const wage_pay =
    Number(raw.wage_pay) > 0 || compensation_type === 'salary'
      ? Number(raw.wage_pay) || derived.wage_pay
      : derived.wage_pay;
  const baseGross =
    Number(raw.forecasted_gross_pay) > 0
      ? Number(raw.forecasted_gross_pay) - payroll_adjustment_net
      : roundPayrollMoney(wage_pay + allowance_pay);
  const forecasted_gross_pay = roundPayrollMoney(baseGross + payroll_adjustment_net);

  return {
    id: raw.id,
    name: raw.name,
    position: String(raw.position ?? '').trim(),
    color_idx: Number.isFinite(Number(raw.color_idx)) ? Number(raw.color_idx) : 0,
    is_archived: Boolean(raw.is_archived),
    compensation_type,
    hourly_wage,
    monthly_salary,
    allowance_per_hour,
    vma_tax_free_per_day,
    overnight_tax_free_per_day,
    work_days: allowancesFromApi.work_days,
    hours_by_type,
    allowance_hours,
    net_work_hours: derived.net_work_hours,
    billable_hours: derived.billable_hours,
    allowance_hourly_pay: allowancesFromApi.allowance_hourly_pay,
    vma_pay: allowancesFromApi.vma_pay,
    overnight_pay: allowancesFromApi.overnight_pay,
    allowance_pay,
    wage_pay,
    total_hours: derived.billable_hours,
    payroll_adjustment_net,
    forecasted_gross_pay,
  };
}

export type EditTarget = {
  employeeId: string;
  employeeName: string;
  entryDate: string;
  /** Viewport-Koordinaten des „Bearbeiten“-Buttons beim Öffnen. */
  anchor: { top: number; left: number; width: number; height: number };
};

const PAYROLL_EDIT_POPOVER_W = 320;
const PAYROLL_EDIT_POPOVER_MAX_H = 400;
const PAYROLL_EDIT_POPOVER_GAP = 8;

/** Pop-over links oberhalb der linken oberen Ecke des Anker-Buttons. */
export function layoutPayrollEditPopover(anchor: EditTarget['anchor']): {
  left: number;
  bottom: number;
  width: number;
  maxHeight: number;
} {
  const margin = 8;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 768;
  const width = PAYROLL_EDIT_POPOVER_W;
  const maxHeight = Math.min(
    PAYROLL_EDIT_POPOVER_MAX_H,
    Math.max(200, anchor.top - PAYROLL_EDIT_POPOVER_GAP - margin),
  );
  const bottom = vh - anchor.top + PAYROLL_EDIT_POPOVER_GAP;
  let left = anchor.left - width - PAYROLL_EDIT_POPOVER_GAP;
  if (left < margin) {
    left = Math.max(margin, anchor.left);
  }
  if (left + width > vw - margin) {
    left = vw - margin - width;
  }
  return { left, bottom, width, maxHeight };
}

/** Leere Auswahl oder alle IDs = gesamter Kandidatenkreis (kein employeeIds-Filter). */
export function buildExportParams(
  from: string,
  to: string,
  selectedEmployeeIds: string[],
  allCandidateIds: string[],
): URLSearchParams {
  const params = new URLSearchParams({ from, to });
  if (isFilterMultiSelectNone(selectedEmployeeIds)) {
    params.set('employeeIds', '');
    return params;
  }
  const isAll =
    selectedEmployeeIds.length === 0 ||
    (allCandidateIds.length > 0 && selectedEmployeeIds.length >= allCandidateIds.length);
  if (!isAll && selectedEmployeeIds.length > 0) {
    params.set('employeeIds', selectedEmployeeIds.join(','));
  }
  return params;
}

export function isEmployeeIncluded(
  employeeId: string,
  selectedEmployeeIds: string[],
  allCandidateIds: string[],
): boolean {
  if (isFilterMultiSelectNone(selectedEmployeeIds)) return false;
  if (selectedEmployeeIds.length === 0 || selectedEmployeeIds.length >= allCandidateIds.length) return true;
  return selectedEmployeeIds.includes(employeeId);
}

export function formatIssueDate(ymd: string): string {
  if (!ymd || ymd.length < 10) return '-';
  const [y, m, d] = ymd.slice(0, 10).split('-');
  return `${d}.${m}.${y}`;
}

export function formatHours(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '-';
  return n.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

export function formatEuro(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '-';
  return n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

export function formatSignedEuro(n: number): string {
  if (!Number.isFinite(n) || n === 0) return '-';
  const formatted = Math.abs(n).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
  return n > 0 ? `+${formatted}` : `−${formatted}`;
}

export function formatEuroInt(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '-';
  return `${Math.round(n).toLocaleString('de-DE')} €`;
}

export function formatHoursWithUnit(n: number): string {
  const h = formatHours(n);
  return h === '-' ? h : `${h} h`;
}

export function issueRowKey(issue: PayrollExportIssue, idx: number): string {
  return `${issue.employeeId}-${issue.entryDate}-${issue.entryType}-${issue.message}-${idx}`;
}

export function canEditIssue(issue: PayrollExportIssue): boolean {
  return Boolean(issue.employeeId && issue.entryDate);
}

export function issueTypeLabel(entryType: string): string {
  if (entryType === 'stammdaten') return 'Stammdaten';
  if (entryType && entryType in ENTRY_TYPE_LABEL) {
    return ENTRY_TYPE_LABEL[entryType as keyof typeof ENTRY_TYPE_LABEL];
  }
  return entryType || '-';
}

export type PayrollBruttoTooltipLine = {
  kind: 'heading' | 'row' | 'subrow' | 'calc' | 'total';
  text: string;
};

function hLabel(n: number): string {
  const v = formatHours(n);
  return v === '-' ? v : `${v} h`;
}

function euroRate(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '-';
  return n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

/** Mehrzeilige Aufschlüsselung für Brutto-Forecast-Tooltip. */
export function buildPayrollBruttoTooltip(emp: PayrollEmployeeSummary): PayrollBruttoTooltipLine[] | null {
  if (
    emp.forecasted_gross_pay <= 0 &&
    emp.billable_hours <= 0 &&
    emp.allowance_pay <= 0 &&
    !(emp.compensation_type === 'salary' && emp.wage_pay > 0)
  ) {
    return null;
  }

  const lines: PayrollBruttoTooltipLine[] = [];
  const raw = emp.hours_by_type;

  if (raw.work > 0 || raw.overtime_comp > 0) {
    lines.push({ kind: 'heading', text: 'Arbeit' });
    if (raw.work > 0) {
      lines.push({ kind: 'subrow', text: `Erfasst: ${hLabel(raw.work)}` });
    }
    if (raw.overtime_comp > 0) {
      lines.push({ kind: 'subrow', text: `Freizeitausgleich: −${formatHours(raw.overtime_comp)} h` });
    }
    lines.push({ kind: 'row', text: `Netto: ${hLabel(emp.net_work_hours)}` });
  }

  const billableParts: Array<{ label: string; hours: number }> = [
    { label: 'Arbeit (netto)', hours: emp.net_work_hours },
    { label: ENTRY_TYPE_LABEL.vacation, hours: raw.vacation },
    { label: ENTRY_TYPE_LABEL.sickness, hours: raw.sickness },
    { label: ENTRY_TYPE_LABEL.public_holiday, hours: raw.public_holiday },
  ].filter((p) => p.hours > 0);

  if (billableParts.length > 0) {
    lines.push({ kind: 'heading', text: 'Abrechnungsstunden' });
    for (const p of billableParts) {
      lines.push({ kind: 'subrow', text: `${p.label}: ${hLabel(p.hours)}` });
    }
    lines.push({ kind: 'row', text: `Summe: ${hLabel(emp.billable_hours)}` });
  }

  if (emp.compensation_type === 'salary' && emp.wage_pay > 0) {
    const isExactOneMonth =
      emp.monthly_salary > 0 && Math.abs(emp.wage_pay - emp.monthly_salary) < 0.005;
    lines.push({
      kind: 'calc',
      text: isExactOneMonth
        ? `Gehalt: ${formatEuro(emp.wage_pay)}`
        : emp.monthly_salary > 0
          ? `Gehalt: ${formatEuro(emp.wage_pay)} (Fixum ${formatEuro(emp.monthly_salary)}/Monat, anteilig)`
          : `Gehalt: ${formatEuro(emp.wage_pay)}`,
    });
  } else if (emp.wage_pay > 0 && emp.hourly_wage > 0) {
    lines.push({
      kind: 'calc',
      text: `Lohn: ${hLabel(emp.billable_hours)} × ${euroRate(emp.hourly_wage)} = ${formatEuro(emp.wage_pay)}`,
    });
  }

  if (emp.allowance_hourly_pay > 0) {
    const rate =
      emp.allowance_per_hour > 0
        ? `${euroRate(emp.allowance_per_hour)}/h`
        : euroRate(emp.allowance_per_hour);
    lines.push({
      kind: 'calc',
      text: `Zulage/Stunde: ${hLabel(emp.allowance_hours)} × ${rate} = ${formatEuro(emp.allowance_hourly_pay)}`,
    });
  }
  if (emp.vma_pay > 0) {
    const rate = euroRate(emp.vma_tax_free_per_day);
    lines.push({
      kind: 'calc',
      text: `VMA: ${emp.work_days} Tage × ${rate}/Tag = ${formatEuro(emp.vma_pay)}`,
    });
  }
  if (emp.overnight_pay > 0) {
    const rate = euroRate(emp.overnight_tax_free_per_day);
    lines.push({
      kind: 'calc',
      text: `ÜN-Pauschale: ${emp.work_days} Tage × ${rate}/Tag = ${formatEuro(emp.overnight_pay)}`,
    });
  }
  if (emp.allowance_pay > 0 && (emp.allowance_hourly_pay > 0 || emp.vma_pay > 0 || emp.overnight_pay > 0)) {
    const parts = [
      emp.allowance_hourly_pay > 0 ? formatEuro(emp.allowance_hourly_pay) : null,
      emp.vma_pay > 0 ? formatEuro(emp.vma_pay) : null,
      emp.overnight_pay > 0 ? formatEuro(emp.overnight_pay) : null,
    ].filter(Boolean);
    if (parts.length > 1) {
      lines.push({
        kind: 'row',
        text: `Zulagen gesamt: ${parts.join(' + ')} = ${formatEuro(emp.allowance_pay)}`,
      });
    }
  }

  if (emp.payroll_adjustment_net !== 0) {
    lines.push({
      kind: 'calc',
      text: `Be- und Abzüge: ${formatSignedEuro(emp.payroll_adjustment_net)}`,
    });
  }

  lines.push({
    kind: 'total',
    text: `Brutto-Forecast: ${formatEuro(emp.forecasted_gross_pay)}`,
  });

  return lines;
}

/** Aufschlüsselung für Zulagen-Spalte (Info-Tooltip). */
export function buildPayrollZulageTooltip(emp: PayrollEmployeeSummary): PayrollBruttoTooltipLine[] | null {
  if (emp.allowance_pay <= 0) return null;

  const lines: PayrollBruttoTooltipLine[] = [{ kind: 'heading', text: 'Zulagen' }];

  if (emp.allowance_hourly_pay > 0) {
    const rate =
      emp.allowance_per_hour > 0
        ? `${euroRate(emp.allowance_per_hour)}/h`
        : euroRate(emp.allowance_per_hour);
    lines.push({
      kind: 'calc',
      text: `Zulage/Stunde (1000): ${hLabel(emp.allowance_hours)} × ${rate} = ${formatEuro(emp.allowance_hourly_pay)}`,
    });
  }
  if (emp.vma_pay > 0) {
    lines.push({
      kind: 'calc',
      text: `VMA (1002): ${emp.work_days} Tage × ${euroRate(emp.vma_tax_free_per_day)}/Tag = ${formatEuro(emp.vma_pay)}`,
    });
  }
  if (emp.overnight_pay > 0) {
    lines.push({
      kind: 'calc',
      text: `ÜN-Pauschale (1001): ${emp.work_days} Tage × ${euroRate(emp.overnight_tax_free_per_day)}/Tag = ${formatEuro(emp.overnight_pay)}`,
    });
  }

  lines.push({
    kind: 'total',
    text: `Summe Zulagen: ${formatEuro(emp.allowance_pay)}`,
  });

  return lines;
}
